/**
 * AppICEMAP — Stats API
 * Fetch aggregated statistics from Directus reports collection.
 *
 * Directus reports collection fields used:
 *   category (relation), user_created (user relation), status,
 *   confirmation_count, denial_count, date_created
 */

import {
	getClient,
	readItems,
	isAuthenticated,
	getSession,
} from "./directus.js";
import { filterActive } from "../utils/expiry.js";

const REPORTS_COLLECTION = "reports";

/**
 * Consecutive days up to today with at least one report.
 * @param {Array<{date_created?: string}>} reports
 * @returns {number}
 */
function currentStreak(reports) {
	const days = new Set(
		reports.map((r) => r.date_created?.slice(0, 10)).filter(Boolean),
	);
	if (days.size === 0) return 0;

	let streak = 0;
	const cursor = new Date();

	// A report today is not required — yesterday still counts as a live streak
	if (!days.has(cursor.toISOString().slice(0, 10))) {
		cursor.setDate(cursor.getDate() - 1);
		if (!days.has(cursor.toISOString().slice(0, 10))) return 0;
	}

	while (days.has(cursor.toISOString().slice(0, 10))) {
		streak++;
		cursor.setDate(cursor.getDate() - 1);
	}

	return streak;
}

/**
 * Fetch personal stats for the currently authenticated user.
 * @returns {Promise<{
 *   totalReports: number,
 *   confirmed: number,
 *   denied: number,
 *   pending: number,
 *   karma: number,
 *   strikes: number,
 *   last30Days: number
 * } | null>}
 */
export async function fetchPersonalStats() {
	if (!isAuthenticated()) return null;

	const client = getClient();

	try {
		// Fetch all user's reports
		const reports = await client.request(
			readItems(REPORTS_COLLECTION, {
				fields: [
					"id",
					"status",
					"confirmation_count",
					"denial_count",
					"date_created",
				],
				filter: {
					user_created: {
						_eq: "$CURRENT_USER",
					},
				},
				limit: -1,
			}),
		);

		const totalReports = (reports || []).length;
		const confirmed = (reports || []).filter(
			(r) => r.confirmation_count > 0,
		).length;
		const denied = (reports || []).filter((r) => r.denial_count > 0).length;
		const pending = totalReports - confirmed - denied;

		// Last 30 days count
		const thirtyDaysAgo = new Date();
		thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
		const last30Days = (reports || []).filter(
			(r) => r.date_created && new Date(r.date_created) >= thirtyDaysAgo,
		).length;

		// Share of judged reports the community backed. Only counts the ones
		// that actually got a vote, so a pile of pending reports doesn't drag it.
		const judged = confirmed + denied;
		const accuracy = judged > 0 ? Math.round((confirmed / judged) * 100) : null;

		return {
			totalReports,
			confirmed,
			denied,
			pending: Math.max(0, pending),
			karma: 0, // will be filled from user store
			strikes: 0, // will be filled from user store
			last30Days,
			accuracy,
			streak: currentStreak(reports || []),
		};
	} catch (err) {
		console.error("[StatsAPI] fetchPersonalStats error:", err);
		return null;
	}
}

/**
 * Fetch community-wide stats.
 * @returns {Promise<{
 *   totalReports: number,
 *   activeNow: number,
 *   reportsToday: number,
 *   byCategory: Array<{ category: string, label: string, count: number }>,
 *   totalContributors: number
 * } | null>}
 */
export async function fetchCommunityStats() {
	const client = getClient();

	try {
		// Total active reports count
		const allReports = await client.request(
			readItems(REPORTS_COLLECTION, {
				fields: [
					"id",
					"status",
					"date_created",
					"expires_at",
					"category.id",
					"category.slug",
					"category.label",
					// Needed to derive the expiry; without it every report falls
					// back to the default TTL and expired ones keep being counted.
					"category.ttl_minutes",
					"user_created.id",
				],
				filter: {
					status: { _in: ["active", "expiring"] },
				},
				limit: -1,
			}),
		);

		// The `status` filter above cannot catch expired reports (nothing updates
		// that column), so the TTL is applied here like everywhere else.
		const reports = filterActive(allReports || []);
		const totalReports = reports.length;

		// Reports today
		const todayStart = new Date();
		todayStart.setHours(0, 0, 0, 0);
		const reportsToday = reports.filter(
			(r) => r.date_created && new Date(r.date_created) >= todayStart,
		).length;

		// Unique contributors
		const contributors = new Set();
		reports.forEach((r) => {
			if (r.user_created?.id) contributors.add(r.user_created.id);
		});

		// By category (top 5)
		const catMap = new Map();
		reports.forEach((r) => {
			const slug = r.category?.slug || "unknown";
			const label = r.category?.label || "Desconocido";
			if (!catMap.has(slug)) {
				catMap.set(slug, { category: slug, label, count: 0 });
			}
			catMap.get(slug).count++;
		});

		const byCategory = [...catMap.values()].sort((a, b) => b.count - a.count);

		// Real activity per hour of day, from the reports we already downloaded
		const byHour = new Array(24).fill(0);
		reports.forEach((r) => {
			if (!r.date_created) return;
			const h = new Date(r.date_created).getHours();
			if (h >= 0 && h < 24) byHour[h]++;
		});

		const peakHour = byHour.some((n) => n > 0)
			? byHour.indexOf(Math.max(...byHour))
			: null;

		return {
			totalReports,
			activeNow: reports.length,
			reportsToday,
			byCategory,
			topCategory: byCategory[0] || null,
			byHour,
			peakHour,
			totalContributors: contributors.size,
		};
	} catch (err) {
		console.error("[StatsAPI] fetchCommunityStats error:", err);
		return null;
	}
}

/**
 * Fetch user activity timeline grouped by day.
 * @param {number} [days=30] — number of days to look back
 * @returns {Promise<Array<{ date: string, count: number }>>}
 */
export async function fetchActivityTimeline(days = 30) {
	if (!isAuthenticated()) return [];

	const client = getClient();
	const sinceDate = new Date();
	sinceDate.setDate(sinceDate.getDate() - days);

	try {
		const reports = await client.request(
			readItems(REPORTS_COLLECTION, {
				fields: ["date_created"],
				filter: {
					user_created: { _eq: "$CURRENT_USER" },
					date_created: { _gte: sinceDate.toISOString() },
				},
				sort: "date_created",
				limit: -1,
			}),
		);

		// Group by day
		const dayMap = new Map();
		(reports || []).forEach((r) => {
			if (!r.date_created) return;
			const day = r.date_created.slice(0, 10); // YYYY-MM-DD
			dayMap.set(day, (dayMap.get(day) || 0) + 1);
		});

		// Fill in all days in the range
		const result = [];
		for (let i = days - 1; i >= 0; i--) {
			const d = new Date();
			d.setDate(d.getDate() - i);
			const key = d.toISOString().slice(0, 10);
			result.push({ date: key, count: dayMap.get(key) || 0 });
		}

		return result;
	} catch (err) {
		console.error("[StatsAPI] fetchActivityTimeline error:", err);
		return [];
	}
}
